import { QueryHookOptions, QueryResult, useQuery } from '@apollo/client';
import get from 'lodash.get';
import set from 'lodash/fp/set';

export type OptimisticQueryUpdate = (
  proxy: any,
  { data }: { data?: any }
) => void;

interface OptimisticQueryOptions<T, V> extends QueryHookOptions<T, V> {
  dataPath: string;
}

export interface OptimisticQueryResults<T> extends QueryResult<any, any> {
  data: T | undefined;
  getUpdater: <TT>(
    transformCachedResults: (cacheEntry: T, mutationResult: TT) => T
  ) => OptimisticQueryUpdate;
}

const useOptimisticQuery = <T, V = any>(
  query: any,
  options: OptimisticQueryOptions<T, V>
): OptimisticQueryResults<T> => {
  const results = useQuery<T, V>(query, {
    variables: options.variables,
    fetchPolicy: 'cache-first',
    ...options,
  });

  const pathData: T | undefined = get(results.data, options.dataPath);

  return {
    ...results,
    data: pathData,
    getUpdater: <TT>(
      transformCachedResults: (cacheEntry: T, mutationResult: TT) => T
    ): OptimisticQueryUpdate => {
      return (proxy, { data }) => {
        if (!data) return;

        const cacheEntry: any = proxy.readQuery({
          query,
          variables: options.variables,
        });

        const cachedEntryAtPath: T = get(cacheEntry, options.dataPath);
        const transformedCachePathData = transformCachedResults(
          cachedEntryAtPath,
          data
        );

        proxy.writeQuery({
          query,
          variables: options.variables,
          data: set(options.dataPath, transformedCachePathData, cacheEntry),
        });
      };
    },
  };
};

export default useOptimisticQuery;
